import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import {
  CommandService,
  GraphResponse,
  GraphService,
  IntegratorTracker,
  IntegratorTrackerService,
  Log,
  LogsService,
  PagedIntegratorTrackersResponse,
  Position,
  PositionService,
  SiteService as TrackerSiteService,
  TrackerSiteAssignmentRequest,
} from '@wheere/api-tracker';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { TRACKERMANAGER_BASE_PATH } from './trackermanager-base-path.token';
import { TrackerKind } from './baro.model';
import { BarometersQuery, TrackerDeviceDetails } from './device-details.model';
import { errorMessage } from '@wheere-front/shared-util';

/**
 * Filter shape for the `loadTrackers` query. Mirrors the integrator UI filters.
 *
 * <p>{@code organizationId} is intentionally optional — for ORG_* users the backend resolver
 * always wins (JWT-org), so the front never bothers sending it. For PLATFORM_* users the
 * filter component (org-only-visible) supplies it explicitly to scope the listing.
 */
export interface TrackersListQuery {
  search?: string;
  status?: string;
  siteIds?: string[];
  /** PLATFORM_* only — ignored by backend for ORG_* (JWT-org wins). */
  organizationId?: string;
  envs?: string[];
  /**
   * Nature d'appareil (ADR-020). Accepté par trackermanager sur `GET /integrator/trackers`
   * depuis `e156b72` mais absent du contrat openapi et du client généré : envoyé via HttpClient.
   */
  kind?: TrackerKind;
  page?: number;
  size?: number;
  sort?: string;
}

/**
 * V7 integrator-side store for tracker reads + integrator-allowed writes.
 *
 * <p>Deliberately slim: we do NOT carry the inside-only features (firmware OTA, debug toggle,
 * timed-logging, supervised-logging, transfer-organization). The integrator only sees:
 * listing, detail, position (last + history), telemetry, logs (live), site-assignment,
 * live-charging-reset.
 *
 * <p>Built with plain Angular signals (no NgRx SignalStore) on purpose — the integrator only
 * needs a thin shared cache; the heavy entity machinery lives in `inside-devices/trackers.store`.
 */
@Injectable({ providedIn: 'root' })
export class TrackersV7Store {
  // ─────────────────────── Signals (state)

  readonly trackers = signal<IntegratorTracker[]>([]);
  readonly total = signal(0);
  readonly loading = signal(false);
  readonly selectedTracker = signal<IntegratorTracker | null>(null);
  readonly lastPosition = signal<Position | null>(null);
  /** Live GNSS position fed by {@code /topic/tracker/{id}/gnss} (raw GNSS, before fusion). */
  readonly lastGnss = signal<Position | null>(null);
  readonly positionHistory = signal<Position[]>([]);
  readonly logs = signal<Log[]>([]);
  /** Live read-file status, fed by {@code /topic/tracker/{id}/file-status}. */
  readonly fileStatus = signal<string | null>(null);

  /**
   * Live log buffer fed by the page-level WebSocket subscription on
   * {@code /topic/tracker/{id}/logs}. Capped at {@link #MAX_LIVE_LOGS} so a chatty tracker
   * can't blow up memory.
   *
   * <p>Owned by the page (not the {@code tracker-logs-live} component) so logs keep
   * accumulating even when the user is on another tab — switching back to Logs shows the
   * full session, not just lines emitted while the tab was visible.
   */
  readonly liveLogs = signal<string[]>([]);

  /** Live file-content buffer (read-file command response stream). Same lifecycle as
   * {@link #liveLogs} — fed by the page WS sub, capped, cleared on detail page exit. */
  readonly liveFileContent = signal<string[]>([]);

  /** Cap the live buffers — protects the browser when a tracker enters debug-spam mode. */
  private readonly MAX_LIVE_LOGS = 10_000;

  /**
   * Champs du `Tracker` complet absents de `IntegratorTracker` (kind, lastBaro, position fixe,
   * connectivité) pour l'appareil affiché — chargés par {@link loadDeviceDetails}.
   */
  readonly selectedDetails = signal<TrackerDeviceDetails | null>(null);

  /** {@code true} when the last write call returned an error — UI binds this to a toast. */
  readonly lastError = signal<string | null>(null);

  // ─────────────────────── Computed

  readonly hasTrackers = computed(() => this.trackers().length > 0);

  // ─────────────────────── Deps

  #integratorTrackerService = inject(IntegratorTrackerService);
  #positionService = inject(PositionService);
  #commandService = inject(CommandService);
  #logsService = inject(LogsService);
  #siteService = inject(TrackerSiteService);
  #graphService = inject(GraphService);
  #http = inject(HttpClient);
  #basePath = inject(TRACKERMANAGER_BASE_PATH);

  // ─────────────────────── Reads

  async loadTrackers(query: TrackersListQuery = {}): Promise<void> {
    this.loading.set(true);
    try {
      const response = await this.#fetchIntegratorTrackers(query);
      this.trackers.set(response.content ?? []);
      this.total.set(response.totalElements ?? 0);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Nombre de trackers correspondant à la requête (page de 1, `totalElements`) — compteurs de la
   * liste integrator (total, en ligne, démo…). Ne touche pas à {@link trackers} ni à {@link total}.
   */
  async countTrackers(query: Omit<TrackersListQuery, 'page' | 'size' | 'sort'> = {}): Promise<number> {
    const response = await this.#fetchIntegratorTrackers({ ...query, page: 0, size: 1 });
    return response.totalElements ?? 0;
  }

  /**
   * Même requête que {@link loadTrackers}, renvoyée sans être mise en cache : ne touche ni à
   * {@link trackers} ni à {@link total} (palette ⌘K de l'intégrateur, ouverte par-dessus la liste).
   */
  async findTrackers(query: TrackersListQuery = {}): Promise<IntegratorTracker[]> {
    const response = await this.#fetchIntegratorTrackers(query);
    return response.content ?? [];
  }

  #fetchIntegratorTrackers(query: TrackersListQuery): Promise<PagedIntegratorTrackersResponse> {
    // V8.3 — the new IntegratorTracker endpoint takes a single siteId (not a list); we
    // forward the first one if the FE passed several. Multi-site filter on the wire
    // would be a follow-up if we ever wire a multi-site picker here.
    const sitesId = query.siteIds && query.siteIds.length > 0 ? query.siteIds[0] : undefined;
    return query.kind
      ? this.#loadIntegratorTrackersByKind(query, query.kind, sitesId)
      : lastValueFrom(
          this.#integratorTrackerService.getIntegratorTrackers(
            query.search,
            query.status,
            sitesId,
            query.organizationId,
            query.page ?? 0,
            query.size ?? 20,
            query.sort,
            query.envs
          )
        );
  }

  /** Même requête que le client généré, avec `kind` en plus (paramètre non généré, cf. {@link TrackersListQuery.kind}). */
  #loadIntegratorTrackersByKind(
    query: TrackersListQuery,
    kind: TrackerKind,
    sitesId: string | undefined
  ): Promise<PagedIntegratorTrackersResponse> {
    let params = new HttpParams()
      .set('kind', kind)
      .set('page', query.page ?? 0)
      .set('size', query.size ?? 20);
    if (query.search) params = params.set('search', query.search);
    if (query.status) params = params.set('status', query.status);
    if (sitesId) params = params.set('sitesId', sitesId);
    if (query.organizationId) params = params.set('organizationId', query.organizationId);
    if (query.sort) params = params.set('sort', query.sort);
    for (const env of query.envs ?? []) params = params.append('envs', env);
    return firstValueFrom(
      this.#http.get<PagedIntegratorTrackersResponse>(`${this.#basePath}/integrator/trackers`, { params })
    );
  }

  /**
   * Baromètres (`kind = BAROMETER`) d'un site ou d'une organisation, avec position fixe et
   * dernière mesure (`GET /trackers?kind=BAROMETER`, même source que la carte de site d'inside).
   * ORG_* : l'organisation du JWT s'impose côté backend. Renvoie la liste sans la mettre en cache.
   */
  async loadBarometers(query: BarometersQuery = {}): Promise<TrackerDeviceDetails[]> {
    let params = new HttpParams()
      .set('kind', 'BAROMETER')
      .set('page', 0)
      .set('size', query.size ?? 200);
    if (query.siteId) params = params.set('sitesId', query.siteId);
    if (query.organizationId) params = params.set('organizationId', query.organizationId);
    const page = await firstValueFrom(
      this.#http.get<{ content?: TrackerDeviceDetails[] }>(`${this.#basePath}/trackers`, { params })
    );
    return (page.content ?? []).filter(b => !!b.id);
  }

  /**
   * Nature, dernière mesure baro, position fixe et connectivité de l'appareil (`GET /trackers/{id}`,
   * contrôle d'accès PLATFORM_OR_ORG). En cas d'échec le signal reste vide : la fiche retombe sur
   * l'affichage tracker (état d'avant ADR-020) et l'erreur est journalisée.
   */
  async loadDeviceDetails(id: string): Promise<void> {
    try {
      const details = await firstValueFrom(this.#http.get<TrackerDeviceDetails>(`${this.#basePath}/trackers/${id}`));
      this.selectedDetails.set(details);
    } catch (e) {
      this.selectedDetails.set(null);
      console.warn('[trackers] could not load the details (kind, lastBaro, connectivity) of %s', id, e);
    }
  }

  /** Fusionne un patch live (ex. mesure baro STOMP) dans {@link selectedDetails}. */
  patchSelectedDetails(changes: Partial<TrackerDeviceDetails>): void {
    const current = this.selectedDetails();
    if (!current) return;
    this.selectedDetails.set({ ...current, ...changes });
  }

  async loadTrackerById(id: string): Promise<void> {
    const tracker = await lastValueFrom(this.#integratorTrackerService.getIntegratorTrackerById(id));
    this.selectedTracker.set(tracker);
  }

  async loadPositionLast(id: string): Promise<void> {
    try {
      const position = await lastValueFrom(this.#positionService.getLastTrackerPosition(id));
      this.lastPosition.set(position);
    } catch {
      // 204 No Content from the backend → no position yet, leave null.
      this.lastPosition.set(null);
    }
  }

  /**
   * Range-based position history. Backend returns paginated positions sorted by date asc.
   * We pull the first page only — the slider in `position-history.component` works on this slice.
   * Date pickers default to the last 24h.
   */
  async loadPositionHistory(id: string, startDate: Date, endDate?: Date): Promise<void> {
    const response = await lastValueFrom(
      this.#positionService.getPositionsByDateRange(id, startDate, endDate, 'BOTH', undefined, 0, 500, 'date,asc')
    );
    this.positionHistory.set(response.content ?? []);
  }

  async loadLogs(id: string): Promise<void> {
    const logs = await lastValueFrom(this.#logsService.getTrackerLogs(id));
    this.logs.set(logs);
  }

  /**
   * Time-series graphs for the tracker (battery, pressure, RSSI, …) over a date range.
   * {@code reduce} = 0 means "raw points". Same endpoint backs the inside Health/Telemetry tab.
   */
  async loadTrackerGraphs(id: string, startDate: Date, endDate: Date, reduce = 0): Promise<GraphResponse> {
    return lastValueFrom(this.#graphService.getTrackerGraphData(id, startDate, endDate, reduce));
  }

  // ─────────────────────── Writes

  async assignToSite(trackerId: string, siteId: string | null): Promise<void> {
    this.lastError.set(null);
    try {
      const body: TrackerSiteAssignmentRequest = { siteId: siteId ?? undefined };
      await lastValueFrom(this.#siteService.assignToSite(trackerId, body));
      // Refresh the selected tracker so the UI picks up the new site link.
      await this.loadTrackerById(trackerId);
    } catch (e: unknown) {
      this.lastError.set(errorMessage(e, 'Failed to assign tracker to site'));
      throw e;
    }
  }

  /**
   * Reboot the tracker — wired to the generated {@code resetTracker} command. Method name
   * kept as {@code liveChargingReset} for backwards compat with the V8 hero-button name,
   * but it's only the reset path now (LIVE / CHRG live in {@link #setMode}).
   * ORG_OPERATOR+ / PLATFORM_ADMIN.
   */
  async liveChargingReset(trackerId: string): Promise<void> {
    this.lastError.set(null);
    try {
      await lastValueFrom(this.#commandService.resetTracker(trackerId));
    } catch (e: unknown) {
      this.lastError.set(errorMessage(e, 'Failed to send reset command'));
      throw e;
    }
  }

  /**
   * Switch the tracker into {@code live} (live-tracking) or {@code chrg} (charging) mode.
   * {@code channel} is the transport — the integrator only uses {@code 4G}; the generated
   * client signature supports {@code LORA} too but that's reserved to inside admin
   * (different operational persona, different acknowledgement flow).
   */
  async setMode(trackerId: string, mode: 'live' | 'chrg', channel: '4G' | 'LORA' = '4G'): Promise<void> {
    this.lastError.set(null);
    try {
      await lastValueFrom(this.#commandService.setMode(trackerId, mode, channel));
    } catch (e: unknown) {
      this.lastError.set(errorMessage(e, `Failed to send ${mode.toUpperCase()} command`));
      throw e;
    }
  }

  /**
   * Streams telemetry / sync data via a one-shot HTTP fetch. Real-time updates go through
   * the shared websocket service (see {@code apps/wheere-integrator/src/app/data/integrator-websocket.service.ts}).
   * Returned shape is left {@code unknown} — the {@code telemetry-chart} component is responsible
   * for projecting series.
   */
  async loadTelemetrySnapshot(trackerId: string): Promise<unknown[]> {
    return firstValueFrom(this.#http.get<unknown[]>(`${this.#basePath}/trackers/${trackerId}/telemetry`));
  }

  // ─────────────────────── Live patches (fed by WebSocket subscriptions in the page)

  /**
   * Spread {@code changes} onto the currently-selected tracker. No-op when nothing is
   * selected (race window during navigation: a WS frame may arrive after
   * {@link #resetSelected} but before the next {@link #loadTrackerById} resolves —
   * dropping it is safer than mutating a stale tracker).
   */
  patchSelectedTracker(changes: Partial<IntegratorTracker>): void {
    const current = this.selectedTracker();
    if (!current) return;
    this.selectedTracker.set({ ...current, ...changes });
  }

  /** Append log lines (already split / cleaned by the caller) to the live buffer. */
  appendLiveLogs(lines: string[]): void {
    if (!lines.length) return;
    const merged = [...this.liveLogs(), ...lines];
    this.liveLogs.set(merged.length > this.MAX_LIVE_LOGS ? merged.slice(-this.MAX_LIVE_LOGS) : merged);
  }

  clearLiveLogs(): void {
    this.liveLogs.set([]);
  }

  appendFileContent(lines: string[]): void {
    if (!lines.length) return;
    const merged = [...this.liveFileContent(), ...lines];
    this.liveFileContent.set(merged.length > this.MAX_LIVE_LOGS ? merged.slice(-this.MAX_LIVE_LOGS) : merged);
  }

  clearFileContent(): void {
    this.liveFileContent.set([]);
  }

  /** Replace the last position (called by both initial fetch and the live position WS sub). */
  setLastPosition(position: Position): void {
    this.lastPosition.set(position);
  }

  setLastGnss(position: Position): void {
    this.lastGnss.set(position);
  }

  setFileStatus(status: string | null): void {
    this.fileStatus.set(status);
  }

  // ─────────────────────── Reset on navigation

  resetSelected(): void {
    this.selectedTracker.set(null);
    this.selectedDetails.set(null);
    this.lastPosition.set(null);
    this.lastGnss.set(null);
    this.positionHistory.set([]);
    this.logs.set([]);
    this.liveLogs.set([]);
    this.liveFileContent.set([]);
    this.fileStatus.set(null);
    this.lastError.set(null);
  }
}
